import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Search, X, RotateCcw, Check } from 'lucide-react';
import { Card, Btn, Tag, Skeleton } from '../ui/primitives';
import { fetchPrompts, updatePrompt, resetPrompt } from '../services/promptsApi';
import DashboardNavbar from '../dashboardNavbar';
import { SYSTEM_TABS } from '../admin/adminSectionTabs';

/**
 * System → Prompts (platform super admins / userType 2).
 *
 * Lists every prompt that drives an OpenAI call, grouped by category, and lets a
 * super admin edit the template body. Edits are saved to the DB and picked up on
 * the next request; "Reset" restores the in-code default.
 */

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 220px;
  svg { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: ${({ theme }) => theme.textSubtle}; }
  input {
    width: 100%;
    padding: 10px 12px 10px 36px;
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 10px;
    font-size: 13.5px;
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.text};
  }
`;

const Group = styled.div`
  margin-top: 18px;
  &:first-child { margin-top: 0; }
`;

const GroupHead = styled.div`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.textSubtle};
  margin: 0 0 8px 2px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;
  th, td { text-align: left; padding: 11px 12px; border-bottom: 1px solid ${({ theme }) => theme.border}; vertical-align: top; }
  th { font-size: 11.5px; text-transform: uppercase; letter-spacing: 0.04em; color: ${({ theme }) => theme.textSubtle}; font-weight: 700; }
  tr:last-child td { border-bottom: none; }
  .key { font-family: ${({ theme }) => theme.fontMono || 'monospace'}; font-size: 12px; color: ${({ theme }) => theme.textMuted}; }
  .label { font-weight: 600; color: ${({ theme }) => theme.text}; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(16, 18, 33, 0.55);
  display: grid;
  place-items: center;
  padding: 20px;
  z-index: 1300;
`;

const Modal = styled.div`
  width: min(760px, 100%);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
  overflow: hidden;
`;

const ModalHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.border};
  h3 { margin: 0 0 3px; font-size: 15px; }
  .k { font-family: ${({ theme }) => theme.fontMono || 'monospace'}; font-size: 12px; color: ${({ theme }) => theme.textMuted}; }
  button { background: none; border: none; cursor: pointer; color: ${({ theme }) => theme.textSubtle}; padding: 2px; }
`;

const ModalBody = styled.div`
  padding: 18px 20px;
  overflow-y: auto;
  .desc { font-size: 12.5px; color: ${({ theme }) => theme.textMuted}; margin: 0 0 12px; line-height: 1.5; }
  textarea {
    width: 100%;
    min-height: 320px;
    resize: vertical;
    padding: 12px;
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 10px;
    font-family: ${({ theme }) => theme.fontMono || 'monospace'};
    font-size: 12.5px;
    line-height: 1.6;
    background: ${({ theme }) => theme.surface2 || theme.surface};
    color: ${({ theme }) => theme.text};
    white-space: pre;
    overflow-wrap: normal;
    overflow-x: auto;
  }
`;

const ModalFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid ${({ theme }) => theme.border};
  .right { display: flex; gap: 8px; }
`;

const Msg = styled.p`
  margin: 10px 2px 0;
  font-size: 13px;
  color: ${({ $error, theme }) => ($error ? '#ef4444' : theme.accentStrong || '#1b7f4b')};
`;

function PromptLibrary() {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null); // the prompt row being edited
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [modalMsg, setModalMsg] = useState('');

  const userId = localStorage.getItem('userId') || '';

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await fetchPrompts();
      setPrompts(rows);
    } catch (e) {
      setError(e.message || 'Could not load prompts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return prompts;
    return prompts.filter((p) =>
      [p.promptKey, p.label, p.category, p.description]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q)));
  }, [prompts, query]);

  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((p) => {
      const cat = p.category || 'other';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(p);
    });
    return Array.from(map.entries());
  }, [filtered]);

  const openEditor = (p) => {
    setEditing(p);
    setDraft(p.content || '');
    setModalMsg('');
  };

  const closeEditor = () => {
    if (saving) return;
    setEditing(null);
    setDraft('');
    setModalMsg('');
  };

  const applyUpdated = (saved) => {
    setPrompts((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    setModalMsg('');
    try {
      const saved = await updatePrompt(editing.id, draft, userId);
      applyUpdated(saved);
      setEditing(null);
      setDraft('');
    } catch (e) {
      setModalMsg(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!editing) return;
    setSaving(true);
    setModalMsg('');
    try {
      const saved = await resetPrompt(editing.id, userId);
      applyUpdated(saved);
      setDraft(saved.content || '');
      setModalMsg('Restored to the built-in default.');
    } catch (e) {
      setModalMsg(e.message || 'Reset failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card $pad>
      <Bar>
        <SearchWrap>
          <Search size={15} />
          <input
            placeholder="Search prompts…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </SearchWrap>
        <Tag>{prompts.length} prompt{prompts.length === 1 ? '' : 's'}</Tag>
      </Bar>

      {error && <Msg $error>{error}</Msg>}

      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Skeleton $w="100%" $h="42px" />
          <Skeleton $w="100%" $h="42px" />
          <Skeleton $w="100%" $h="42px" />
        </div>
      ) : (
        !error && grouped.length === 0 ? (
          <p style={{ fontSize: 13, color: '#7e8298' }}>No prompts match “{query}”.</p>
        ) : (
          grouped.map(([cat, rows]) => (
            <Group key={cat}>
              <GroupHead>{cat}</GroupHead>
              <Table>
                <thead>
                  <tr>
                    <th style={{ width: '46%' }}>Prompt</th>
                    <th style={{ width: '18%' }}>Status</th>
                    <th style={{ width: '18%' }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => (
                    <tr key={p.id}>
                      <td>
                        <div className="label">{p.label || p.promptKey}</div>
                        <div className="key">{p.promptKey}</div>
                      </td>
                      <td>
                        {p.updatedBy
                          ? <Tag>Edited</Tag>
                          : <span style={{ color: '#7e8298', fontSize: 12.5 }}>Default</span>}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <Btn $variant="ghost" onClick={() => openEditor(p)}>Edit</Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Group>
          ))
        )
      )}

      {editing && (
        <Overlay onMouseDown={closeEditor}>
          <Modal onMouseDown={(e) => e.stopPropagation()}>
            <ModalHead>
              <div>
                <h3>{editing.label || editing.promptKey}</h3>
                <span className="k">{editing.promptKey}</span>
              </div>
              <button type="button" aria-label="Close" onClick={closeEditor}><X size={18} /></button>
            </ModalHead>
            <ModalBody>
              {editing.description && <p className="desc">{editing.description}</p>}
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                spellCheck={false}
              />
              {modalMsg && <Msg $error={/fail/i.test(modalMsg)}>{modalMsg}</Msg>}
            </ModalBody>
            <ModalFoot>
              <Btn $variant="ghost" onClick={handleReset} disabled={saving}>
                <RotateCcw size={14} /> Reset to default
              </Btn>
              <div className="right">
                <Btn $variant="ghost" onClick={closeEditor} disabled={saving}>Cancel</Btn>
                <Btn onClick={handleSave} disabled={saving || draft === editing.content}>
                  {saving ? 'Saving…' : <><Check size={14} /> Save</>}
                </Btn>
              </div>
            </ModalFoot>
          </Modal>
        </Overlay>
      )}
    </Card>
  );
}

export default function PromptsManager() {
  return (
    <div>
      <DashboardNavbar menuItems={SYSTEM_TABS} />
      <Shell>
        <Inner>
          <PageIntro>
            <h2>Prompts</h2>
            <p>
              Every prompt sent to the AI. Edit a template to change how the AI is instructed — changes apply on the
              next request. Placeholders like <code>{'{{courseName}}'}</code> are filled in per request; leave them intact.
            </p>
          </PageIntro>
          <PromptLibrary />
        </Inner>
      </Shell>
    </div>
  );
}

const Shell = styled.div`
  padding: 0 28px 36px;
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};
  @media (max-width: 768px) {
    padding: 0 14px 28px;
  }
`;

const Inner = styled.div`
  width: min(1180px, 95%);
  margin: 0 auto;
`;

const PageIntro = styled.div`
  margin: 4px 0 22px;

  h2 {
    margin: 0 0 6px;
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.fsSm};
    color: ${({ theme }) => theme.textMuted};
    line-height: 1.5;
  }
`;
